/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import axios from 'axios';
import { IRequestContext, msdyn365Commerce, IRequestContextForSegment } from '@msdyn365-commerce/core-internal';
import { IInternalTelemetry, LogLevel } from '@msdyn365-commerce/telemetry-internal';

interface ISegmentProvider {
    providerId: string;
    segmentIds: string[];
}

const REQUEST_CONTEXT_PROPERTIES_FOR_SEG = ['clientIPAddress', 'cookieString', 'cookie', 'query', 'params', 'apiSettings', 'features'];

export const resolveMissingSegmentsOnClient = (requestContext: IRequestContext, userId: string, telemetry: IInternalTelemetry) => {
    if (!requestContext || !requestContext.missingSegments || requestContext.missingSegments.length) {
        return;
    }
    let domain = requestContext.canonicalDomain || '';
    if (msdyn365Commerce.isBrowser) {
        domain = window.location.hostname;
    }
    const missingSegments: ISegmentProvider[] = [];
    const neededMissingSegments = requestContext.missingSegments;
    Object.keys(neededMissingSegments).forEach((providerId: string) => {
        const segmentProvider: ISegmentProvider = {
            providerId: providerId,
            segmentIds: neededMissingSegments[providerId]
        };
        missingSegments.push(segmentProvider);
    });
    // if there is no misisng segments. return.
    if (missingSegments.length === 0) {
        return;
    }
    // add cookie string. So we are able to re-construct cookie obj when oneRf call Node to resolve segments.
    requestContext.cookieString = (document && document.cookie) || '';
    const segmentRequestId = requestContext.segmentRequestId;
    const missingSegmentResolvingUrl = `https://${domain}/_msdyn365/getsegment`;

    // only maintain selected properties of requestContext.
    const segRequestContext: IRequestContextForSegment = {};
    REQUEST_CONTEXT_PROPERTIES_FOR_SEG.forEach((property: string) => {
        if (property in requestContext && !!requestContext[property]) {
            segRequestContext[property] = requestContext[property];
        }
    });

    const requestContextObject = requestContext?.features?.disable_segment_requestContext ? requestContext : segRequestContext;

    axios({
        method: 'post',
        url: missingSegmentResolvingUrl,
        data: {
            segmentProviderList: missingSegments,
            userId: userId,
            requestContext: requestContextObject,
            ...(segmentRequestId && { segmentRequestId: segmentRequestId })
        }
    })
        // after post request, oneRf will process the request. Node will not receive any response.
        .then()
        .catch(err => telemetry.log(LogLevel.Error, `Error in resolving segments from AJAX call for ${domain}`, { exception: err }));
};
